import { createApp } from 'vue'
import { createPinia } from 'pinia'
import router from './router'
import './style.css'
import App from './App.vue'
import { useSettingsStore } from './stores/settingsStore'
import { sanitizeHtml } from './utils/sanitize'

const app = createApp(App)
const pinia = createPinia()

app.use(pinia)
app.use(router)

// Global Safe HTML Directive (replaces v-html with DOMPurify sanitization)
app.directive('safe-html', {
    mounted(el, binding) {
        el.innerHTML = sanitizeHtml(binding.value);
    },
    updated(el, binding) {
        if (binding.value !== binding.oldValue) {
            el.innerHTML = sanitizeHtml(binding.value);
        }
    }
});

app.config.globalProperties.$sanitize = sanitizeHtml;

// ========== Global Client Error Reporting ==========
const reportClientError = (err, info = '') => {
    try {
        const payload = {
            message: err?.message || String(err),
            stack: err?.stack || null,
            url: window.location.href,
            componentName: info || 'VueApp',
            userAgent: navigator.userAgent
        }
        if (navigator.sendBeacon) {
            const blob = new Blob([JSON.stringify(payload)], { type: 'application/json' })
            navigator.sendBeacon('/api/system/client-logs', blob)
        } else {
            fetch('/api/system/client-logs', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
                keepalive: true
            }).catch(() => {})
        }
    } catch (e) {
        // Silent catch
    }
}

app.config.errorHandler = (err, instance, info) => {
    console.error('Vue Global Error Handler:', err, info)
    const componentName = instance?.$options?.name || instance?.$options?.__name || 'VueComponent'
    reportClientError(err, `${componentName} (${info})`)
}

window.addEventListener('unhandledrejection', (event) => {
    reportClientError(event.reason || 'Unhandled Promise Rejection', 'UnhandledPromiseRejection')
})

// Suppress known Chrome DevTools soft-navigation / Web Vitals internal bug (reportAllChanges reading startTime)
const isChromeDevToolsSoftNavError = (msg) => {
    const s = String(msg || '')
    return s.includes('reportAllChanges') || s.includes('startTime') || s.includes('devToolsReportSoftNavs')
}

const prevOnError = window.onerror
window.onerror = function(msg, url, lineNo, columnNo, error) {
    if (isChromeDevToolsSoftNavError(msg) || isChromeDevToolsSoftNavError(error?.message)) {
        return true // Returning true suppresses the error in DevTools console
    }
    if (typeof prevOnError === 'function') {
        return prevOnError.apply(this, arguments)
    }
    return false
}

window.addEventListener('error', (event) => {
    const msg = event.message || event.error?.message || ''
    if (isChromeDevToolsSoftNavError(msg)) {
        event.preventDefault()
        event.stopImmediatePropagation()
        return
    }
    reportClientError(event.error || event.message, 'WindowError')
}, true)

// Mount app immediately to achieve fast FCP/LCP without waiting on network waterfall
app.mount('#app')

// Fetch public settings from database asynchronously to initialize dynamic dependencies
const loadPublicSettings = async () => {
    try {
        const response = await fetch('/api/settings/public')
        if (response.ok) {
            const data = await response.json()
            if (data.success && data.data) {
                // Allow LoginModal and other components to know about public settings
                app.config.globalProperties.$pubSettings = data.data

                // Initialize global settings store (e.g., online_shopping_enabled)
                const settingsStore = useSettingsStore()
                settingsStore.initializeSettings(data.data)
            }
        }
    } catch (error) {
        console.warn('Failed to fetch public settings:', error)
    }
}

loadPublicSettings()

// Global PWA prompt listener for Admin App
window.addEventListener('beforeinstallprompt', (e) => {
    if (window.location.pathname.startsWith('/admin')) {
        e.preventDefault()
        window.deferredAdminPrompt = e
        window.dispatchEvent(new CustomEvent('adminPromptAvailable'))
    }
})
